import filter from 'lodash/filter';
import keys from 'lodash/keys';
import includes from 'lodash/includes';
import every from 'lodash/every';

import type { AppData, SelectedServiceType } from '../types/app_data';

import { ENABLE_REFI } from './constants';

/**
 * Given appData.selectedService, returns an array of the selected services.
 */
export const getSelectedServicesArray = (
  selectedService: AppData['selectedService']
): Array<keyof SelectedServiceType> => {
  return filter(
    keys(selectedService || {}) as unknown as Array<keyof SelectedServiceType>,
    (key) => !!selectedService?.[key]
  );
};

/**
 * Returns true if the only selected services are the ones passed as arguments.
 *
 * @param appData - The app data object containing the selected services.
 * @param services - An array of services to check if they are selected.
 * @returns A boolean indicating whether the passed services are the only selected services.
 */
export const areOnlySelectedServices = (appData: AppData, services: Array<keyof SelectedServiceType>): boolean => {
  const selectedServices = getSelectedServicesArray(appData.selectedService);

  if (selectedServices.length !== services.length) {
    return false;
  }

  return !selectedServices.some((key) => !includes(services, key));
};

/**
 * Auto selected the services if user is from some specific pages.
 */
export const autoSelectedServicesByReferer = (referer = ''): Partial<SelectedServiceType> => {
  const autoSelectedServices: Partial<SelectedServiceType> = {};
  const isFromRefi = !!referer?.match?.(
    /\/(auto-refi|car-loan|auto-loan-refinance|important-car-loan-terms|car-loan-by-state|car-loan-by-vehicle|save-money-on-your-car-loan)/
  );

  /**
   * No need to ad ABTest here
   */
  if (isFromRefi && ENABLE_REFI) {
    autoSelectedServices.refi = true;
  }

  const isFromGarage = !!referer?.match?.(
    /\/(car-repair|types-of-car-repair|car-repair-by-vehicle|car-repair-by-state|save-money-on-car-repair)/
  );

  if (isFromGarage) {
    autoSelectedServices.garage = true;
  }

  return autoSelectedServices;
};

/**
 * Return true, if no service is selected.
 */
export const noServiceSelected = (appData: AppData): boolean => {
  return !appData.selectedService || every(appData.selectedService, (value) => !value);
};

/**
 * Return true, if user only selected insurance service.
 */
export const isOnlyInsuranceSelected = (appData: AppData): boolean => {
  return areOnlySelectedServices(appData, ['insurance']) || noServiceSelected(appData);
};
